import { useEffect, useRef, useState } from "react";
import { Bot, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { API_BASE, api, formatApiErrorDetail } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const PROMPTS = ["Plan a 4-week release strategy for my next single", "Write a playlist curator pitch template", "Give me 5 TikTok hooks for my latest track", "Polish my artist bio"];

export const CopilotDrawer = ({ open, onOpenChange }) => {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => { if (open) api.get("/copilot/history").then((r) => setMsgs(r.data)).catch(() => {}); }, [open]);
  useEffect(() => end.current?.scrollIntoView({ behavior: "smooth" }), [msgs]);
  const send = async (q) => {
    const message = (q ?? text).trim();
    if (!message || busy) return;
    setText("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: message }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API_BASE}/api/copilot/chat`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message, lang: localStorage.getItem("sonora_lang") || "en" }) });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(formatApiErrorDetail(j.detail));
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const data = p.replace(/^data: /, "");
          if (data === "[DONE]") continue;
          const j = JSON.parse(data);
          if (j.error) throw new Error(j.error);
          setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: c[c.length - 1].text + j.delta }; return c; });
        }
      }
    } catch (e) {
      toast.error(e.message);
      setMsgs((m) => m.slice(0, -1));
    } finally { setBusy(false); }
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-testid="copilot-drawer" className="bg-[#0E0E15] border-white/10 w-full sm:max-w-lg flex flex-col p-0">
        <SheetHeader className="p-6 border-b border-white/10">
          <SheetTitle className="font-display text-2xl flex items-center gap-2"><Bot className="text-[var(--hub)]" /> Artist Co-Pilot</SheetTitle>
          <SheetDescription>Release strategy, promo pitches & social advice — tuned to your catalog.</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-auto p-6 space-y-4" data-testid="copilot-messages">
          {msgs.length === 0 && (
            <div className="space-y-2">{PROMPTS.map((p) => <button key={p} data-testid="copilot-suggestion" onClick={() => send(p)} className="w-full text-left p-3 rounded-xl border border-white/10 hover:border-[var(--hub)] text-sm flex gap-2"><Sparkles size={14} className="text-[var(--hub)] mt-0.5 shrink-0" />{p}</button>)}</div>
          )}
          {msgs.map((m, i) => (
            <div key={i} className={`max-w-[90%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "ml-auto bg-[var(--hub)] text-white" : "bg-white/[0.06]"}`}>
              {m.text || <span className="inline-flex gap-1">{[0, 1, 2].map((d) => <span key={d} className="w-1.5 h-4 bg-white/60 rounded eq-bar" style={{ animationDelay: `${d * 150}ms` }} />)}</span>}
            </div>
          ))}
          <div ref={end} />
        </div>
        <div className="p-4 border-t border-white/10 flex gap-2">
          <input data-testid="copilot-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask your Co-Pilot…" className="flex-1 h-11 px-4 rounded-full bg-white/5 border border-white/10 focus:border-[var(--hub)] outline-none text-sm" />
          <button data-testid="copilot-send-button" disabled={busy || !text.trim()} onClick={() => send()} className="w-11 h-11 rounded-full bg-[var(--hub)] disabled:opacity-40 flex items-center justify-center"><Send size={16} /></button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
